import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminUniversities() {
  const { token } = useAuth();
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    campuses: '',
    status: 'active'
  });
  
  // Selection states
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUniversities();
  }, []);

  const fetchUniversities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/universities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUniversities(response.data);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to fetch universities');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      name: formData.name,
      campuses: formData.campuses.split(',').map(c => c.trim()).filter(c => c),
      status: formData.status
    };

    try {
      if (editingUniversity) {
        await axios.put(
          `${API_URL}/api/universities/${editingUniversity.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('University updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/universities`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('University created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchUniversities();
    } catch (error) {
      toast.error('Failed to save university');
    }
  };

  const handleEdit = (university) => {
    setEditingUniversity(university);
    setFormData({
      name: university.name,
      campuses: university.campuses.join(', '),
      status: university.status
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this university?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/universities/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('University deleted');
      fetchUniversities();
    } catch (error) {
      toast.error('Failed to delete university');
    }
  };

  const resetForm = () => {
    setEditingUniversity(null);
    setFormData({ name: '', campuses: '', status: 'active' });
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredUniversities.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredUniversities.map(u => u.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    setDeleting(true);
    try {
      await axios.post(`${API_URL}/api/admin/universities/bulk-delete`, 
        { ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${selectedIds.length} university(ies) deleted`);
      setDeleteDialogOpen(false);
      fetchUniversities();
    } catch (error) {
      toast.error('Failed to delete universities');
    } finally {
      setDeleting(false);
    }
  };

  const filteredUniversities = universities.filter(uni =>
    uni.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-heading font-bold text-2xl">Universities</h2>
            <p className="text-muted-foreground">Manage partner universities</p>
          </div>
          <div className="flex gap-2">
            {selectedIds.length > 0 && (
              <Button 
                variant="destructive" 
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="bulk-delete-universities"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected ({selectedIds.length})
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-lime-500 hover:bg-lime-600 text-white" data-testid="add-university">
                  <Plus className="w-4 h-4 mr-2" />
                  Add University
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingUniversity ? 'Edit University' : 'Add University'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">University Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter university name"
                      required
                      data-testid="university-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="campuses">Campuses (comma-separated)</Label>
                    <Input
                      id="campuses"
                      value={formData.campuses}
                      onChange={(e) => setFormData({ ...formData, campuses: e.target.value })}
                      placeholder="Main Campus, Downtown Campus"
                      data-testid="university-campuses"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger data-testid="university-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-lime-500 hover:bg-lime-600 text-white" data-testid="save-university">
                      {editingUniversity ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="font-heading">All Universities</CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-64"
                  data-testid="search-universities"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filteredUniversities.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No universities found</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedIds.length === filteredUniversities.length && filteredUniversities.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-universities"
                        />
                      </TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Campuses</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUniversities.map((uni) => (
                      <TableRow key={uni.id} data-testid={`university-row-${uni.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(uni.id)}
                            onCheckedChange={() => toggleSelect(uni.id)}
                            data-testid={`select-university-${uni.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{uni.name}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {uni.campuses.length > 0 ? uni.campuses.map((campus, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {campus}
                              </Badge>
                            )) : '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={uni.status === 'active' ? 'default' : 'secondary'}>
                            {uni.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(uni.created_at).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(uni)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(uni.id)} className="text-destructive">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Bulk Delete</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Are you sure you want to delete {selectedIds.length} university(ies)? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleBulkDelete}
              disabled={deleting}
              data-testid="confirm-bulk-delete-universities"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
